// react 
import React, { useEffect } from "react";
// css
import "./App.css";
// browserrouter 
import { BrowserRouter as Router, Route, Routes, useLocation } from "react-router-dom";
// Components
import Header from './Component/Header';
import Footer from "./Component/Footer";
// pages
import Home from "./pages/Home";
// About pages
import AboutUs from "./pages/About/AboutUs";
import Blog from "./pages/About/Blog";
import BlogCategory from "./pages/About/BlogCategory";
import Contact from "./pages/About/Contact";
// Shop pages
import Shop from "./pages/Shop/Shop";
import ShopGridCol3 from "./pages/Shop/ShopGridCol3";
import ShopListCol from "./pages/Shop/ShopListCol";
import ShopCart from "./pages/Shop/ShopCart";
import AntiGaspi from "./pages/Shop/AntiGaspi";
import ShopCheckOut from "./pages/Shop/ShopCheckOut";
import ShopWishList from "./pages/Shop/ShopWishList";
// Store pages
import StoreList from "./pages/store/StoreList";
import SingleShop from "./pages/store/SingleShop";
// Account pages
import PilotageDashboard from "./pages/Accounts/Pilotage/PilotageDashboard";
import PilotageSynthese from "./pages/Accounts/Pilotage/PilotageSynthese";
import PilotageEncours from "./pages/Accounts/Pilotage/PilotageEncours";
import PilotageCompta from "./pages/Accounts/Pilotage/PilotageCompta";
import MyAccountCashier from "./pages/Accounts/Cashier/Cashier";
import MyAccountSetting from "./pages/Accounts/MyAcconutSetting";
import MyAcconutNotification from "./pages/Accounts/MyAcconutNotification";
import MyAcconutPaymentMethod from "./pages/Accounts/MyAcconutPaymentMethod";
import MyAccountAddress from "./pages/Accounts/MyAccountAddress";
import MyAccountCustomers from "./pages/Accounts/MyAccountCustomers";
import MyAccountCustomerForm from "./pages/Accounts/MyAccountCustomerForm";
import MyAccountSAVDetail from "./pages/Accounts/MyAccountSAVDetail";
import MyAccountSuppliers from "./pages/Accounts/MyAccountSuppliers";
import MyAccountB2B from "./pages/Accounts/MyAccountB2B";
import MyAccountForgetPassword from "./pages/Accounts/MyAccountForgetPassword";
import MyAccountSignIn from "./pages/Accounts/MyAccountSignIn";
import MyAccountSignUp from "./pages/Accounts/MyAccountSignUp";
import AccessDenied from "./pages/Accounts/AccessDenied";
import ErrorBoundary from "./Component/ErrorBoundary";
import FAQ from "./pages/FooterElements/Faq";
import Coupons from "./pages/FooterElements/Coupons";
import Careers from "./pages/FooterElements/Careers";
import HelpCenter from "./pages/FooterElements/HelpCenter";
import ProtectedAdminRoute from "./Component/ProtectedAdminRoute";
import CookieConsent from "./Component/CookieConsent";
import { AuthProvider } from "./context/AuthContext";
import ChatbotWidget from "./Component/ChatbotWidget";

const SEO_CONFIG = {
  "/": {
    title: "Almoune | Epicerie libanaise et orientale",
    description:
      "Almoune, epicerie libanaise et orientale a Evry-Courcouronnes. Produits libanais, douceurs, cafe, condiments et livraison locale.",
    seoPath: "/",
  },
  "/Shop": {
    title: "Boutique Almoune | Produits libanais et orientaux",
    description:
      "Parcourez la boutique Almoune : epicerie libanaise, cafe, boissons, douceurs, condiments et produits orientaux avec stock mis a jour.",
    seoPath: "/boutique",
  },
  "/AntiGaspi": {
    title: "Anti-gaspi | Almoune",
    description:
      "Produits à date courte ou emballage abîmé vendus à prix réduits chez Almoune pour lutter contre le gaspillage alimentaire.",
    seoPath: "/anti-gaspi",
  },
  "/AboutUs": {
    title: "A propos | Almoune",
    description:
      "Decouvrez Almoune, boutique specialisee en produits libanais et orientaux a Evry-Courcouronnes.",
    seoPath: "/a-propos",
  },
  "/Contact": {
    title: "Contact Almoune | Epicerie libanaise a Evry-Courcouronnes",
    description:
      "Contactez Almoune pour vos commandes, renseignements et informations sur la boutique et la livraison.",
    seoPath: "/contact",
  },
};

const applySeoTag = (selector, attribute, value) => {
  const element = document.querySelector(selector);
  if (!element) {
    return;
  }
  element.setAttribute(attribute, value);
};

const buildStructuredData = (seo) => {
  const baseUrl = "https://almoune.fr";
  const pageUrl = `${baseUrl}${seo.seoPath || "/"}`;

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "GroceryStore",
        "@id": `${baseUrl}/#store`,
        name: "Almoune",
        url: baseUrl,
        telephone: "+33981391433",
        image: `${baseUrl}/logo512.png`,
        description:
          "Epicerie libanaise et orientale a Evry-Courcouronnes avec produits du quotidien, douceurs, cafe, boissons et livraison locale.",
        address: {
          "@type": "PostalAddress",
          streetAddress: "2 Pl. du Rouillon",
          postalCode: "91000",
          addressLocality: "Evry-Courcouronnes",
          addressCountry: "FR",
        },
        areaServed: "Evry-Courcouronnes",
      },
      {
        "@type": "WebSite",
        "@id": `${baseUrl}/#website`,
        url: baseUrl,
        name: "Almoune",
        inLanguage: "fr-FR",
      },
      {
        "@type": "WebPage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name: seo.title,
        description: seo.description,
        isPartOf: { "@id": `${baseUrl}/#website` },
        about: { "@id": `${baseUrl}/#store` },
        inLanguage: "fr-FR",
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumb`,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Accueil",
            item: baseUrl,
          },
          ...(seo.seoPath && seo.seoPath !== "/"
            ? [
                {
                  "@type": "ListItem",
                  position: 2,
                  name: seo.title.replace(" | Almoune", "").replace("Almoune | ", ""),
                  item: pageUrl,
                },
              ]
            : []),
        ],
      },
    ],
  };
};

const AppLayout = () => {
  const location = useLocation();
  const isAdminRoute =
    location.pathname.startsWith("/MyAccount") ||
    location.pathname.startsWith("/MyAcconut");

  useEffect(() => {
    const fallbackSeo = {
      title: "Almoune",
      description:
        "Almoune, boutique de produits libanais et orientaux a Evry-Courcouronnes.",
      seoPath: "/",
    };
    const seo = SEO_CONFIG[location.pathname] || fallbackSeo;
    const canonicalUrl = `https://almoune.fr${seo.seoPath || "/"}`;

    document.title = seo.title;
    applySeoTag('meta[name="description"]', "content", seo.description);
    applySeoTag('meta[property="og:title"]', "content", seo.title);
    applySeoTag('meta[property="og:description"]', "content", seo.description);
    applySeoTag('meta[name="twitter:title"]', "content", seo.title);
    applySeoTag('meta[name="twitter:description"]', "content", seo.description);

    let canonicalElement = document.querySelector('link[rel="canonical"]');
    if (!canonicalElement) {
      canonicalElement = document.createElement("link");
      canonicalElement.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalElement);
    }
    canonicalElement.setAttribute("href", canonicalUrl);
    applySeoTag('meta[property="og:url"]', "content", canonicalUrl);

    let structuredDataElement = document.getElementById("almoune-structured-data");
    if (!structuredDataElement) {
      structuredDataElement = document.createElement("script");
      structuredDataElement.setAttribute("type", "application/ld+json");
      structuredDataElement.setAttribute("id", "almoune-structured-data");
      document.head.appendChild(structuredDataElement);
    }
    structuredDataElement.textContent = JSON.stringify(buildStructuredData(seo));
  }, [location.pathname]);

  return (
    <>
      {!isAdminRoute ? <Header /> : null}
      <Routes>
        <Route path="/" element={<Home />} />
        {/* Shop pages */}
        <Route path="/Shop" element={<Shop />} />
        <Route path="/AntiGaspi" element={<AntiGaspi />} />
        <Route path="/ShopGridCol3" element={<ShopGridCol3 />} />
        <Route path="/ShopListCol" element={<ShopListCol />} />
        <Route path="/ShopWishList" element={<ShopWishList />} />
        <Route path="/ShopCheckOut" element={<ShopCheckOut />} />
        <Route path="/ShopCart" element={<ShopCart />} />
        {/* Store pages */}
        <Route path="/StoreList" element={<StoreList />} />
        <Route path="/SingleShop" element={<SingleShop />} />
        {/* Accounts pages */}
        <Route path="/Pilotage" element={<ProtectedAdminRoute><PilotageDashboard /></ProtectedAdminRoute>} />
        <Route path="/Pilotage/Synthese" element={<ProtectedAdminRoute><PilotageSynthese /></ProtectedAdminRoute>} />
        <Route path="/Pilotage/Encours" element={<ProtectedAdminRoute><PilotageEncours /></ProtectedAdminRoute>} />
        <Route path="/Pilotage/Compta" element={<ProtectedAdminRoute><PilotageCompta /></ProtectedAdminRoute>} />
        <Route path="/MyAccountCashier" element={<ProtectedAdminRoute><MyAccountCashier /></ProtectedAdminRoute>} />
        <Route
          path="/MyAccountSetting"
          element={
            <ProtectedAdminRoute
              allowedRoles={["admin"]}
              title="Page reservee aux administrateurs"
              message="Seuls les administrateurs peuvent modifier les parametres, zones et regles globales."
            >
              <MyAccountSetting />
            </ProtectedAdminRoute>
          }
        />
        <Route path="/MyAcconutNotification" element={<ProtectedAdminRoute><MyAcconutNotification /></ProtectedAdminRoute>} />
        <Route path="/MyAcconutPaymentMethod" element={<ProtectedAdminRoute><MyAcconutPaymentMethod /></ProtectedAdminRoute>} />
        <Route path="/MyAccountAddress" element={<ProtectedAdminRoute><MyAccountAddress /></ProtectedAdminRoute>} />
<Route
          path="/MyAccountCustomers"
          element={
            <ProtectedAdminRoute
              allowedRoles={["admin"]}
              title="Page reservee aux administrateurs"
              message="Seuls les administrateurs peuvent gerer les clients."
            >
              <MyAccountCustomers />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/MyAccountCustomerForm"
          element={
            <ProtectedAdminRoute
              allowedRoles={["admin"]}
              title="Page reservee aux administrateurs"
              message="Seuls les administrateurs peuvent gerer les clients."
            >
              <MyAccountCustomerForm />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/MyAccountCustomerForm/:id"
          element={
            <ProtectedAdminRoute
              allowedRoles={["admin"]}
              title="Page reservee aux administrateurs"
              message="Seuls les administrateurs peuvent gerer les clients."
            >
              <MyAccountCustomerForm />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/MyAccountSAVDetail/:id"
          element={
            <ProtectedAdminRoute
              allowedRoles={["admin"]}
              title="Page reservee aux administrateurs"
              message="Seuls les administrateurs peuvent gerer le service client (SAV)."
            >
              <MyAccountSAVDetail />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/MyAccountSuppliers"
          element={
            <ProtectedAdminRoute
              allowedRoles={["admin"]}
              title="Page reservee aux administrateurs"
              message="Seuls les administrateurs peuvent gerer les fournisseurs."
            >
              <MyAccountSuppliers />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/MyAccountB2B"
          element={
            <ProtectedAdminRoute
              allowedRoles={["admin"]}
              title="Page reservee aux administrateurs"
              message="Seuls les administrateurs peuvent gerer le B2B."
            >
              <MyAccountB2B />
            </ProtectedAdminRoute>
          }
        />
        <Route path="/MyAccountForgetPassword" element={<MyAccountForgetPassword />} />
        <Route path="/MyAccountSignIn" element={<MyAccountSignIn />} />
        <Route path="/MyAccountSignUp" element={<MyAccountSignUp />} />
        <Route path="/AccessDenied" element={<AccessDenied />} />
        {/* About pages */}
        <Route path="/Blog" element={<Blog />} />
        <Route path="/BlogCategory" element={<BlogCategory />} />
        <Route path="/Contact" element={<Contact />} />
        <Route path="/AboutUs" element={<AboutUs />} />
        {/* Footer Elements */}
        <Route path="/Faq" element={<FAQ />} />
        <Route path="/Coupons" element={<Coupons />} />
        <Route path="/Careers" element={<Careers />} />
        <Route path="/helpcenter" element={<HelpCenter />} />
      </Routes>
      {!isAdminRoute ? <Footer /> : null}
      <CookieConsent />
      <ChatbotWidget />
    </>
  );
};

const App = () => {
  return (
    <div>
      <AuthProvider>
      <Router>
        <ErrorBoundary>
          <AppLayout />
        </ErrorBoundary>
      </Router>
      </AuthProvider>
    </div>
  );
};

export default App;


